import { useEffect, useRef, useState } from "react";
import {
  Camera,
  Image as ImageIcon,
  Plus,
  Trash2,
  Upload,
  Video as VideoIcon,
  FileText,
  Mic,
  X,
  Loader2,
  ExternalLink,
} from "lucide-react";
import api from "../lib/api";
import { uploadFile, uploadUrl } from "../lib/uploads";
import VoiceRecorder from "./VoiceRecorder";
import Modal from "./Modal";
import { toast } from "./ui/sonner";
import UploadScanBadge from "./uploads/UploadScanBadge";

// Wave 30 Chunk 10v — extract upload id from a gallery item's file_url.
function uploadIdOf(item) {
  if (!item) return null;
  if (item.upload_id) return item.upload_id;
  const url = item.file_url || "";
  if (!url) return null;
  if (url.startsWith("/api/uploads/")) return url.replace("/api/uploads/", "").split(/[/?#]/)[0];
  if (url.startsWith("/uploads/")) return url.replace("/uploads/", "").split(/[/?#]/)[0];
  return url; // bare fid
}

/**
 * LeadMediaBlock — gallery section for a lead.
 * Reuses /api/gallery-items with `lead_id` so media auto-promotes onto
 * the customer record once the lead links to one.  Supports:
 *   • Camera capture (mobile-first via accept="*" capture)
 *   • Multi-select gallery upload (images, videos, docs)
 *   • Voice notes (audio recorder)
 *   • Inline preview gallery + delete
 */
export default function LeadMediaBlock({ leadId, customerId, canEdit = true, isManager = false }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [voiceOpen, setVoiceOpen] = useState(false);
  const [preview, setPreview] = useState(null);
  const fileRef = useRef(null);
  const cameraRef = useRef(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/gallery-items", { params: { lead_id: leadId } });
      setItems(r.data || []);
    } catch (e) {
      toast.error("Couldn't load lead media.");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    if (leadId) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [leadId]);

  const handleFiles = async (files) => {
    const list = Array.from(files || []);
    if (list.length === 0) return;
    setUploading(true);
    try {
      for (const f of list) {
        const up = await uploadFile(f);
        const media_type = (up.kind === "image" || up.kind === "video" || up.kind === "audio")
          ? up.kind
          : "other";
        await api.post("/gallery-items", {
          lead_id: leadId,
          customer_id: customerId || null,
          file_url: `/api/uploads/${up.id}`,
          file_name: up.filename,
          file_size: up.size,
          media_type,
          title: up.filename,
        });
      }
      toast.success(`Uploaded ${list.length} item${list.length === 1 ? "" : "s"}.`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed.");
    } finally {
      setUploading(false);
    }
  };

  const handleVoiceCommit = async (blob, duration_seconds) => {
    setVoiceOpen(false);
    if (!blob) return;
    try {
      // Upload blob via the standard /api/uploads endpoint
      const file = new File([blob], `voice-note-${Date.now()}.webm`, { type: blob.type || "audio/webm" });
      const up = await uploadFile(file);
      await api.post("/gallery-items", {
        lead_id: leadId,
        customer_id: customerId || null,
        file_url: `/api/uploads/${up.id}`,
        file_name: up.filename,
        file_size: up.size,
        media_type: "audio",
        title: `Voice note · ${Math.round((duration_seconds || 0))}s`,
      });
      toast.success("Voice note saved.");
      await load();
    } catch (e) {
      toast.error("Couldn't save voice note.");
    }
  };

  const remove = async (it) => {
    if (!window.confirm("Remove this item?")) return;
    try {
      await api.delete(`/gallery-items/${it.id}`);
      setItems((prev) => prev.filter((x) => x.id !== it.id));
    } catch (e) {
      toast.error("Could not remove item.");
    }
  };

  const fileSrc = (it) => {
    const url = it.file_url || "";
    if (url.startsWith("/api/uploads/")) {
      return uploadUrl(url.replace("/api/uploads/", ""));
    }
    return url;
  };

  return (
    <div className="hv-card p-4" data-testid="lead-media-block">
      <div className="flex items-center justify-between gap-2 mb-3">
        <div>
          <div className="font-display font-bold flex items-center gap-2">
            <ImageIcon className="w-4 h-4 text-[#1FB6C1]" /> Media & Files
          </div>
          <div className="text-[11px] text-[#a0aabf]">
            Photos, videos, documents and voice notes for this lead.
            {customerId && (
              <span> Auto-mirrors to the customer record.</span>
            )}
          </div>
        </div>
        <span className="text-[10px] uppercase tracking-wider text-[#647087]">
          {items.length} item{items.length === 1 ? "" : "s"}
        </span>
      </div>

      {canEdit && (
        <div className="flex flex-wrap gap-2 mb-3">
          <button
            type="button"
            onClick={() => cameraRef.current?.click()}
            disabled={uploading}
            className="hv-btn hv-btn-ghost text-xs"
            data-testid="lead-media-camera"
          >
            <Camera className="w-3.5 h-3.5" /> Camera
          </button>
          <button
            type="button"
            onClick={() => fileRef.current?.click()}
            disabled={uploading}
            className="hv-btn hv-btn-ghost text-xs"
            data-testid="lead-media-upload"
          >
            {uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
            {uploading ? "Uploading…" : "Upload media"}
          </button>
          <button
            type="button"
            onClick={() => setVoiceOpen(true)}
            disabled={uploading}
            className="hv-btn hv-btn-ghost text-xs"
            data-testid="lead-media-voice"
          >
            <Mic className="w-3.5 h-3.5" /> Voice note
          </button>
          <input
            ref={cameraRef}
            type="file"
            accept="image/*,video/*"
            capture="environment"
            multiple
            className="hidden"
            onChange={(e) => handleFiles(e.target.files)}
            data-testid="lead-media-camera-input"
          />
          <input
            ref={fileRef}
            type="file"
            accept="image/*,video/*,application/pdf,.doc,.docx,.xls,.xlsx,.txt"
            multiple
            className="hidden"
            onChange={(e) => handleFiles(e.target.files)}
            data-testid="lead-media-file-input"
          />
        </div>
      )}

      {loading ? (
        <div className="text-[#647087] text-xs py-6 text-center">Loading…</div>
      ) : items.length === 0 ? (
        <div className="text-[#647087] text-xs py-6 text-center border border-dashed border-white/10 rounded-md">
          {canEdit
            ? "No media yet — drop photos, videos, docs or voice notes above."
            : "No media yet."}
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">
          {items.map((it) => (
            <MediaTile
              key={it.id}
              item={it}
              src={fileSrc(it)}
              canDelete={canEdit && (isManager || it.uploaded_by_role !== "client")}
              onOpen={() => setPreview(it)}
              onRemove={() => remove(it)}
            />
          ))}
        </div>
      )}

      {voiceOpen && (
        <Modal open onClose={() => setVoiceOpen(false)} title="Record voice note" testId="lead-voice-modal">
          <VoiceRecorder
            onRecorded={handleVoiceCommit}
            onCancel={() => setVoiceOpen(false)}
          />
        </Modal>
      )}

      {preview && <MediaPreview item={preview} src={fileSrc(preview)} onClose={() => setPreview(null)} />}
    </div>
  );
}

function MediaTile({ item, src, canDelete, onOpen, onRemove }) {
  const t = item.media_type || "other";
  return (
    <div
      className="relative aspect-square rounded-md overflow-hidden border border-white/10 bg-[#0a0b0e] group"
      data-testid={`lead-media-tile-${item.id}`}
    >
      <button
        type="button"
        onClick={onOpen}
        className="absolute inset-0 w-full h-full"
      >
        {t === "image" ? (
          <img src={src} alt={item.title || ""} className="w-full h-full object-cover" loading="lazy" />
        ) : t === "video" ? (
          <div className="w-full h-full flex items-center justify-center bg-[#1FB6C1]/5">
            <VideoIcon className="w-8 h-8 text-[#1FB6C1]" />
          </div>
        ) : t === "audio" ? (
          <div className="w-full h-full flex items-center justify-center bg-[#A78BFA]/5">
            <Mic className="w-8 h-8 text-[#A78BFA]" />
          </div>
        ) : (
          <div className="w-full h-full flex items-center justify-center bg-[#F59E0B]/5">
            <FileText className="w-8 h-8 text-[#F59E0B]" />
          </div>
        )}
        <div className="absolute inset-x-0 bottom-0 p-1.5 bg-gradient-to-t from-black/80 to-transparent text-[10px] text-white truncate">
          {item.title || item.file_name}
        </div>
      </button>
      {canDelete && (
        <button
          type="button"
          onClick={onRemove}
          className="absolute top-1 right-1 w-6 h-6 rounded bg-rose-500/30 hover:bg-rose-500/50 text-white opacity-0 group-hover:opacity-100 transition flex items-center justify-center"
          data-testid={`lead-media-delete-${item.id}`}
          title="Remove"
        >
          <Trash2 className="w-3 h-3" />
        </button>
      )}
      {/* Wave 30 Chunk 10v — async Vision scan progress for image tiles */}
      {t === "image" && uploadIdOf(item) && (
        <span className="absolute bottom-1 right-1 pointer-events-auto">
          <UploadScanBadge
            uploadId={uploadIdOf(item)}
            initialStatus={item.vision_describe_status || null}
            compact
          />
        </span>
      )}
    </div>
  );
}

function MediaPreview({ item, src, onClose }) {
  const t = item.media_type || "other";
  return (
    <Modal open onClose={onClose} title={item.title || item.file_name || "Media"} testId="lead-media-preview" maxWidth="max-w-3xl">
      <div className="space-y-2">
        {t === "image" && <img src={src} alt={item.title || ""} className="max-w-full max-h-[70vh] mx-auto rounded" />}
        {t === "video" && <video src={src} controls className="max-w-full max-h-[70vh] mx-auto rounded" />}
        {t === "audio" && <audio src={src} controls className="w-full" />}
        {t !== "image" && t !== "video" && t !== "audio" && (
          <div className="text-center py-12">
            <FileText className="w-12 h-12 mx-auto text-[#F59E0B] mb-2" />
            <div className="text-sm text-[#a0aabf] mb-3">{item.file_name}</div>
          </div>
        )}
        <div className="flex justify-end gap-2 pt-2 border-t border-white/5">
          <a
            href={src}
            target="_blank"
            rel="noopener noreferrer"
            className="hv-btn hv-btn-ghost text-xs"
          >
            <ExternalLink className="w-3 h-3" /> Open original
          </a>
        </div>
      </div>
    </Modal>
  );
}
